import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './DealTracker.css';

function DealTracker({ transactionId, transaction, userType, onStepComplete }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const defaultSteps = [
    { order: 1, name: 'Offer Accepted', short: 'Offer', description: 'Purchase offer accepted by seller' },
    { order: 2, name: 'Earnest Money', short: 'Earnest', description: 'Buyer deposits earnest money' },
    { order: 3, name: 'Option Period', short: 'Option', description: 'Inspections and due diligence' },
    { order: 4, name: 'Title Work', short: 'Title', description: 'Title search and commitment' },
    { order: 5, name: 'Appraisal', short: 'Appraisal', description: 'Property appraisal completed' },
    { order: 6, name: 'Loan Approval', short: 'Loan', description: 'Final loan approval received' },
    { order: 7, name: 'Clear to Close', short: 'Clear', description: 'All conditions satisfied' },
    { order: 8, name: 'Closing', short: 'Closed', description: 'Documents signed, deed recorded' }
  ];

  const shortLabels = ['Offer', 'Earnest', 'Option', 'Title', 'Appraisal', 'Loan', 'Clear', 'Closed'];

  useEffect(() => {
    if (transactionId) {
      fetchSteps();
    } else {
      setSteps(defaultSteps.map(s => ({ ...s, status: 'pending' })));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  const fetchSteps = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/dealtracker/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.steps && response.data.steps.length > 0) {
        setSteps(response.data.steps);
      } else {
        const initialSteps = defaultSteps.map((s, i) => ({ 
          ...s, 
          status: i === 0 ? 'completed' : i === 1 ? 'in_progress' : 'pending' 
        }));
        setSteps(initialSteps);
      }
    } catch (error) {
      console.error('Error fetching steps:', error);
      if (transactionId) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`/api/dealtracker/${transactionId}/initialize`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchSteps();
          return;
        } catch (e) {
          console.error('Error initializing steps:', e);
        }
      }
      const initialSteps = defaultSteps.map((s, i) => ({ 
        ...s, 
        status: i === 0 ? 'completed' : i === 1 ? 'in_progress' : 'pending' 
      }));
      setSteps(initialSteps);
    } finally {
      setLoading(false);
    }
  };

  const handleStepClick = async (step, index) => {
    if (!transactionId || updating) return;
    if (step.status !== 'in_progress') return;
    const stepOrder = step.step_order || index + 1;
    
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/dealtracker/${transactionId}/complete/${stepOrder}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (stepOrder === 8 && onStepComplete) {
        await fetchSteps();
        setTimeout(() => onStepComplete(), 10000);
      } else {
        await fetchSteps();
      }
    } catch (error) {
      console.error('Error completing step:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAnalyzeClick = () => {
    setShowUploadModal(true);
    setSelectedFile(null);
    setAnalysisResult(null);
    setError(null);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Please upload a PDF file');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Please upload a PDF file');
      }
    }
  };

  const extractTextFromPDF = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const typedArray = new Uint8Array(e.target.result);
          
          if (!window.pdfjsLib) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            document.head.appendChild(script);
            await new Promise(res => script.onload = res);
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          }
          
          const pdf = await window.pdfjsLib.getDocument(typedArray).promise;
          let fullText = '';
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\n';
          }
          
          resolve(fullText);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleAnalyzeContract = async () => {
    if (!selectedFile) return;

    setAnalyzing(true);
    setError(null);

    try {
      const text = await extractTextFromPDF(selectedFile);
      
      if (!text || text.trim().length < 100) {
        throw new Error('Could not extract text from PDF. Please ensure it contains readable text.');
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/contracts/extract-dates/${transactionId}`,
        { contractText: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAnalysisResult(response.data.dates);
      
      if (onStepComplete) {
        onStepComplete();
      }
    } catch (err) {
      console.error('Contract analysis error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to analyze contract. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return null;
  }

  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const totalSteps = steps.length;
  const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  
  const currentStep = steps.find(s => s.status === 'in_progress') || steps.find(s => s.status === 'pending');
  const currentIndex = steps.findIndex(s => s.status === 'in_progress' || s.status === 'pending');
  const nextStep = currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;

  return (
    <div className="deal-tracker-minimal">
      {/* Upload Modal - rendered via Portal to escape overflow:hidden containers */}
      {showUploadModal && ReactDOM.createPortal(
        <div className="contract-modal-overlay" onClick={() => !analyzing && setShowUploadModal(false)}>
          <div className="contract-modal" onClick={e => e.stopPropagation()}>
            <div className="contract-modal-header">
              <h2>Analyze Contract with AI</h2>
              <p>Upload your signed contract to automatically extract all important dates and deadlines</p>
              {!analyzing && (
                <button className="modal-close-btn" onClick={() => setShowUploadModal(false)}>×</button>
              )}
            </div>
            
            <div className="contract-modal-body">
              {!analysisResult ? (
                <>
                  {/* Drag and Drop Zone */}
                  <div 
                    className={`dropzone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="application/pdf"
                      style={{ display: 'none' }}
                    />
                    
                    {selectedFile ? (
                      <div className="file-selected">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <path d="M9 15l2 2 4-4"/>
                        </svg>
                        <p className="file-name">{selectedFile.name}</p>
                        <p className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div className="dropzone-content">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="12" y1="18" x2="12" y2="12"/>
                          <line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                        <p className="dropzone-text">Drag and drop your contract here</p>
                        <p className="dropzone-subtext">or <span>browse</span> to select a file</p>
                        <p className="dropzone-hint">PDF files only</p>
                      </div>
                    )}
                  </div>

                  {error && <div className="upload-error">{error}</div>}

                  <button 
                    className="analyze-btn"
                    onClick={handleAnalyzeContract}
                    disabled={!selectedFile || analyzing}
                  >
                    {analyzing ? (
                      <>
                        <span className="btn-spinner"></span>
                        Analyzing Contract...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 3v18M3 12h18M5.5 5.5l13 13M18.5 5.5l-13 13"/>
                        </svg>
                        Analyze Contract
                      </>
                    )}
                  </button>
                </>
              ) : (
                /* Results View */
                <div className="analysis-results">
                  <div className="results-header">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <h3>Contract Analyzed Successfully!</h3>
                    <p>The following dates and deadlines were extracted:</p>
                  </div>

                  <div className="results-grid">
                    <div className="result-card">
                      <span className="result-label">Closing Date</span>
                      <span className="result-value">{formatDate(analysisResult.closing_date)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">Sale Price</span>
                      <span className="result-value">{formatCurrency(analysisResult.sale_price)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">Earnest Money</span>
                      <span className="result-value">{formatCurrency(analysisResult.earnest_money_amount)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">{analysisResult.due_diligence_end ? 'Due Diligence Ends' : 'Option Period Ends'}</span>
                      <span className="result-value">{formatDate(analysisResult.due_diligence_end || analysisResult.option_period_end)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">Execution Date</span>
                      <span className="result-value">{formatDate(analysisResult.execution_date)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">Financing Deadline</span>
                      <span className="result-value">{formatDate(analysisResult.financing_approval_deadline)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">Title Commitment</span>
                      <span className="result-value">{formatDate(analysisResult.title_commitment_deadline)}</span>
                    </div>
                    <div className="result-card">
                      <span className="result-label">{analysisResult.inspection_objection_deadline ? 'Inspection Objection' : 'Inspection Deadline'}</span>
                      <span className="result-value">{formatDate(analysisResult.inspection_objection_deadline || analysisResult.inspection_deadline || analysisResult.appraisal_deadline)}</span>
                    </div>
                    {analysisResult.financing_type && (
                      <div className="result-card">
                        <span className="result-label">Financing Type</span>
                        <span className="result-value" style={{ textTransform: 'capitalize' }}>{analysisResult.financing_type.replace('_', ' ')}</span>
                      </div>
                    )}
                    {analysisResult.due_diligence_fee && (
                      <div className="result-card">
                        <span className="result-label">Due Diligence Fee</span>
                        <span className="result-value">{formatCurrency(analysisResult.due_diligence_fee)}</span>
                      </div>
                    )}
                    {analysisResult.possession_date && (
                      <div className="result-card">
                        <span className="result-label">Possession Date</span>
                        <span className="result-value">{formatDate(analysisResult.possession_date)}</span>
                      </div>
                    )}
                    {analysisResult.final_walkthrough_date && (
                      <div className="result-card">
                        <span className="result-label">Final Walk-Through</span>
                        <span className="result-value">{formatDate(analysisResult.final_walkthrough_date)}</span>
                      </div>
                    )}
                  </div>

                  {analysisResult.notes && (
                    <div className="results-notes">
                      <strong>Notes:</strong> {analysisResult.notes}
                    </div>
                  )}

                  <button className="done-btn" onClick={() => setShowUploadModal(false)}>
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Large percentage */}
      <div className="tracker-percentage">
        <span className="percentage-number">{progress}</span>
        <span className="percentage-symbol">%</span>
      </div>
      <div className="tracker-subtitle">{progress === 100 ? 'Closed' : 'toward closing'}</div>

      {/* Segmented progress bar with labels */}
      <div className="tracker-segments-container">
        {/* Deadline dates row */}
        <div className="tracker-deadlines">
          {steps.map((step, index) => (
            <div key={`deadline-${index}`} className="tracker-deadline">
              {step.deadline ? formatShortDate(step.deadline) : ''}
            </div>
          ))}
        </div>
        <div className="tracker-segments">
          {steps.map((step, index) => (
            <div 
              key={step.id || index} 
              className={`segment ${step.status === 'completed' ? 'completed' : step.status === 'in_progress' ? 'active' : 'pending'} ${step.status === 'in_progress' ? 'clickable' : ''}`}
              title={`${step.step_name || step.name}${step.deadline ? ' - Due: ' + formatShortDate(step.deadline) : ''}${step.status === 'in_progress' ? ' - Click to complete' : ''}`}
              onClick={() => handleStepClick(step, index)}
              style={{ cursor: step.status === 'in_progress' ? 'pointer' : 'default' }}
            />
          ))}
        </div>
        <div className="tracker-labels">
          {steps.map((step, index) => (
            <div 
              key={`label-${index}`} 
              className={`tracker-label ${step.status === 'completed' ? 'completed' : step.status === 'in_progress' ? 'active' : 'pending'}`}
            >
              {shortLabels[index] || step.step_name || step.name}
            </div>
          ))}
        </div>
      </div>

      {/* Current/Next step with centered buttons */}
      <div className="tracker-details-simple">
        <div className="tracker-detail-item">
          <span className="detail-label-inline">Current:</span>
          <span className="detail-value-inline">{currentStep?.step_name || currentStep?.name || 'Complete'}</span>
        </div>
        <div className="tracker-buttons-center">
          {currentStep && transactionId && (
            <button 
              className="complete-step-btn"
              onClick={() => handleStepClick(currentStep, currentIndex)}
              disabled={updating}
              style={{
                padding: '6px 14px',
                fontSize: '12px',
                background: '#1a1a2e',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: updating ? 'not-allowed' : 'pointer',
                opacity: updating ? 0.6 : 1
              }}
            >
              Mark Complete
            </button>
          )}
          <button 
            className="ai-analyze-btn"
            onClick={handleAnalyzeClick}
            style={{
              padding: '6px 14px',
              fontSize: '12px',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v18M3 12h18M5.5 5.5l13 13M18.5 5.5l-13 13"/>
            </svg>
            Analyze Contract with AI
          </button>
        </div>
        <div className="tracker-detail-item">
          <span className="detail-label-inline">Next:</span>
          <span className="detail-value-inline detail-muted">{nextStep?.step_name || nextStep?.name || '—'}</span>
        </div>
      </div>
    </div>
  );
}

export default DealTracker;
